/**
 * fighterBuffOverlay — Readable passive/buff ring + stack pips (SPY-112).
 */
import { getFighterBuffReadability } from '../../domain/champions/fighterBuffReadability.js';
import {
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  SphereGeometry
} from 'three';

const MAX_PIPS = 8;

function resolveThree(THREE) {
  return {
    Group: THREE?.Group || Group,
    Mesh: THREE?.Mesh || Mesh,
    MeshBasicMaterial: THREE?.MeshBasicMaterial || MeshBasicMaterial,
    RingGeometry: THREE?.RingGeometry || RingGeometry,
    SphereGeometry: THREE?.SphereGeometry || SphereGeometry,
    DoubleSide: THREE?.DoubleSide ?? DoubleSide
  };
}

/**
 * @param {typeof import('three')} THREE
 */
export function createFighterBuffOverlay(THREE) {
  const T = resolveThree(THREE);
  const group = new T.Group();
  group.userData = { kind: 'fighter_buff_overlay' };
  const ring = buildRing(T);
  group.add(ring);
  const pips = addPips(T, group);
  group.visible = false;
  return { group, ring, pips, _spin: 0 };
}

function buildRing(T) {
  const mat = new T.MeshBasicMaterial({
    color: 0xb91c1c,
    transparent: true,
    opacity: 0.55,
    side: T.DoubleSide
  });
  const ring = new T.Mesh(
    new T.RingGeometry(0.85, 1.15, 28).rotateX(-Math.PI / 2),
    mat
  );
  ring.position.y = 0.06;
  return ring;
}

function addPips(T, group) {
  const pips = [];
  for (let i = 0; i < MAX_PIPS; i += 1) {
    const pip = new T.Mesh(
      new T.SphereGeometry(0.1, 8, 8),
      new T.MeshBasicMaterial({ color: 0xfca5a5, transparent: true, opacity: 0.9 })
    );
    pip.visible = false;
    group.add(pip);
    pips.push(pip);
  }
  return pips;
}

/**
 * @param {ReturnType<typeof createFighterBuffOverlay>} overlay
 * @param {ReturnType<typeof getFighterBuffReadability>} readability
 * @param {number} dt
 */
export function syncFighterBuffOverlay(overlay, readability, dt = 0) {
  if (!overlay?.group) return;
  if (!readability || (readability.fill <= 0 && readability.stacks <= 0 && !readability.empowered)) {
    overlay.group.visible = false;
    return;
  }
  overlay.group.visible = true;
  const color = readability.color || 0xffffff;
  if (overlay.ring.material) {
    overlay.ring.material.color = color;
    overlay.ring.material.opacity = readability.empowered ? 0.95 : 0.45 + readability.fill * 0.35;
  }
  const scale = 1 + readability.fill * 0.35;
  if (overlay.ring.scale?.setScalar) overlay.ring.scale.setScalar(scale);
  else if (overlay.ring.scale?.set) overlay.ring.scale.set(scale, 1, scale);

  overlay._spin = (overlay._spin || 0) + dt * (readability.empowered ? 3.2 : 1.6);
  const showPips = pipCountFor(readability, overlay.pips.length);
  placePips(overlay.pips, showPips, overlay._spin, color);
}

function pipCountFor(readability, maxPips) {
  if (readability.passiveId === 'pit_grit') {
    return Math.min(maxPips, Math.max(0, Math.round(readability.fill * 5)));
  }
  return Math.min(
    maxPips,
    Math.max(0, Math.round(Math.min(readability.stacks, readability.maxStacks || readability.stacks)))
  );
}

function placePips(pips, count, spin, color) {
  for (let i = 0; i < pips.length; i += 1) {
    const pip = pips[i];
    const on = i < count;
    pip.visible = on;
    if (!on) continue;
    if (pip.material) pip.material.color = color;
    const angle = spin + (i / Math.max(1, count)) * Math.PI * 2;
    const r = 1.35;
    if (pip.position?.set) pip.position.set(Math.cos(angle) * r, 0.45, Math.sin(angle) * r);
  }
}

/**
 * Convenience: sync from live champion entity.
 */
export function syncFighterBuffFromEntity(overlay, entity, dt = 0) {
  syncFighterBuffOverlay(overlay, getFighterBuffReadability(entity), dt);
}
